import axios from 'axios';
import { API_BASE_URL } from './config';

const getVapidPublicKey = () => process.env.REACT_APP_WEBPUSH_VAPID_PUBLIC_KEY || '';
const SERVICE_WORKER_PATH = '/notifications-sw.js';
const SERVICE_WORKER_TIMEOUT_MS = 10000;
const PENDING_RETIREMENT_KEY = 'pendingWebPushRetirementEndpoint';

async function getNotificationServiceWorker() {
  const registration = await navigator.serviceWorker.register(SERVICE_WORKER_PATH);
  let timeoutId;
  try {
    const readyRegistration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((_, reject) => {
        timeoutId = window.setTimeout(() => {
          reject(new Error('Browser notification setup timed out. Please reload the page and try again.'));
        }, SERVICE_WORKER_TIMEOUT_MS);
      }),
    ]);
    return readyRegistration || registration;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

async function refreshNotificationServiceWorker() {
  const registration = await navigator.serviceWorker.register(SERVICE_WORKER_PATH, {
    updateViaCache: 'none',
  });
  await registration.update();
  return registration;
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function getPremiumAccess() {
  const accessToken = window.localStorage.getItem('premiumAccessToken') || '';
  if (!accessToken) {
    throw new Error('Restore your premium access before enabling browser alerts.');
  }
  return accessToken;
}

async function persistWebPushSubscription(email, subscription) {
  const normalizedEmail = (email || '').trim().toLowerCase();
  if (!normalizedEmail) throw new Error('A premium account email is required.');
  const accessToken = getPremiumAccess();

  await axios.post(`${API_BASE_URL}/api/webpush-subscribe`, {
    email: normalizedEmail,
    subscription,
  }, {
    headers: { 'x-premium-access-token': accessToken },
  });
}

export async function subscribeWebPush(email) {
  const normalizedEmail = (email || '').trim().toLowerCase();
  if (!normalizedEmail) throw new Error('A premium account email is required.');

  if (typeof window === 'undefined') throw new Error('Browser notifications are unavailable.');
  getPremiumAccess();
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    throw new Error('This browser does not support web notifications.');
  }

  const vapidPublicKey = getVapidPublicKey();
  if (!vapidPublicKey) {
    throw new Error('Browser notifications are temporarily unavailable. Please try again later.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Browser notification permission was not granted.');
  }

  const registration = await getNotificationServiceWorker();

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
  }

  await persistWebPushSubscription(normalizedEmail, subscription);

  return { ok: true, endpoint: subscription.endpoint };
}

export async function reconcileExistingWebPush(email) {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return { ok: false, reason: 'unsupported' };
  }
  getPremiumAccess();
  const registration = await getNotificationServiceWorker();
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return { ok: false, reason: 'not_subscribed' };

  await persistWebPushSubscription(email, subscription);
  return { ok: true, endpoint: subscription.endpoint };
}

export async function repairWebPush(email) {
  const normalizedEmail = (email || '').trim().toLowerCase();
  if (!normalizedEmail) throw new Error('A premium account email is required.');
  if (typeof window === 'undefined') throw new Error('Browser notifications are unavailable.');
  getPremiumAccess();
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    throw new Error('This browser does not support web notifications.');
  }

  const vapidPublicKey = getVapidPublicKey();
  if (!vapidPublicKey) {
    throw new Error('Browser notifications are temporarily unavailable. Please try again later.');
  }
  if (Notification.permission !== 'granted') {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') throw new Error('Browser notification permission was not granted.');
  }

  const registration = await refreshNotificationServiceWorker();
  const existing = await registration.pushManager.getSubscription();
  const pendingEndpoint = window.localStorage.getItem(PENDING_RETIREMENT_KEY) || '';
  const oldEndpoint = pendingEndpoint || existing?.endpoint || '';
  if (!oldEndpoint) throw new Error('No browser subscription was found to repair.');

  window.localStorage.setItem(PENDING_RETIREMENT_KEY, oldEndpoint);
  if (existing) {
    const removed = await existing.unsubscribe();
    if (!removed) throw new Error('The browser could not retire its stale notification subscription.');
  }

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  });
  const response = await axios.post(`${API_BASE_URL}/api/webpush-repair`, {
    email: normalizedEmail,
    old_endpoint: oldEndpoint,
    subscription,
  }, { headers: premiumHeaders() });
  if (!response?.data?.success) throw new Error('Production did not confirm the repaired browser subscription.');

  window.localStorage.removeItem(PENDING_RETIREMENT_KEY);
  return { ok: true, endpoint: subscription.endpoint, deliveryVerified: false };
}

export async function getCurrentWebPushEndpoint() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return '';
  const registration = await getNotificationServiceWorker();
  const subscription = await registration.pushManager.getSubscription();
  return subscription?.endpoint || '';
}

function premiumHeaders() {
  return { 'x-premium-access-token': getPremiumAccess() };
}

export async function fetchWebPushStatus(email, endpoint = '', testId = '') {
  const response = await axios.post(`${API_BASE_URL}/api/webpush-status`, {
    email: (email || '').trim().toLowerCase(),
    endpoint,
    test_id: testId || undefined,
  }, { headers: premiumHeaders() });
  return response?.data || {};
}

export async function sendWebPushTest(email, endpoint) {
  const response = await axios.post(`${API_BASE_URL}/api/webpush-test`, {
    email: (email || '').trim().toLowerCase(),
    endpoint,
  }, { headers: premiumHeaders() });
  return response?.data || {};
}

export async function hasActiveWebPushSubscription() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    return Boolean(await registration.pushManager.getSubscription());
  } catch (err) {
    return false;
  }
}
